import { parseEntities, parseEntity } from '@/shared/converters/commonConverters';
import { parseMoment } from '@/shared/converters/dateConverters';
import DriveBooking from '../models/DriveBooking';
import DriveAsset from '../models/DriveAsset';
import SyncResponse from '../models/SyncResponse';
import DriveBookingDetails from '../models/DriveBookingDetails';
import DriveRenter from '../models/DriveRenter';
import DriveCheckInOut from '../models/DriveCheckInOut';
import DriveBilling from '../models/DriveBilling';
import DriveEditableSettings from '../models/DriveEditableSettings';
import { parseMediaFiles2 } from '@/general/converters/mediaFileConverters2';
import DriveAvailableAssets, { DriveReservationAsset, DriveReservationAssetClass, DriveReservationAssetGroup, DriveReservationSettings } from '../models/DriveAvailableAssets';

const DRIVE_ASSET_SCHEMA = {
  id: 'id',
  externalClassReference: 'externalClassReference',
  description: 'description',
  identifier: 'identifier',
  registrationNumber: 'registrationNumber',
  type: 'type',
};

const DRIVE_BOOKING_SCHEMA = {
  id: 'id',
  mediaCount: 'mediaCount',
  status: 'status',
  type: 'type',
  scheduledFrom: (data: any) => parseMoment(data.scheduledFrom),
  scheduledTill: (data: any) => parseMoment(data.scheduledTill),
  lastSynchronizedOn: (data: any) => parseMoment(data.lastSynchronizedOn),
  lastSynchronizedBy: 'lastSynchronizedBy',
  checkOutOn: (data: any) => parseMoment(data.checkOutOn),
  checkInOn: (data: any) => parseMoment(data.checkInOn),
  modifiedOn: (data: any) => parseMoment(data.modifiedOn),
  asset: (data: any) => parseAsset(data.asset),
  invoiceStatus: 'invoiceStatus',
};

const DRIVE_RENTER_SCHEMA = {
  id: 'id',
  firstName: 'firstName',
  lastName: 'lastName',
  phone: 'phone',
  email: 'email',
};

const DRIVE_CHECK_IN_OUT_SCHEMA = {
  date: (data: any) => parseMoment(data.date),
  mileage: 'mileage',
  fuelLevel: 'fuelLevel',
  note: 'note',
};

const DRIVE_BILLING_SCHEMA = {
  id: 'id',
  dealerCustomerDataId: 'dealerCustomerDataId',
  debtorType: 'debtorType',
  billingPart: 'billingPart',
  isBillingPercentage: 'isBillingPercentage',
  isInvoiced: 'isInvoiced',
  isDeleted: 'isDeleted',
};

const DRIVE_EDITABLE_SETTINGS_SCHEMA = {
  isStartDateEditable: 'isStartDateEditable',
  isEndDateEditable: 'isEndDateEditable',
  isDriverEditable: 'isDriverEditable',
  isBillingEditable: 'isBillingEditable',
  isRequestedTypeEditable: 'isRequestedTypeEditable',
  canCheckIn: 'canCheckIn',
  canCheckOut: 'canCheckOut',
};

const DRIVE_BOOKING_DETAILS_SCHEMA = {
  id: 'id',
  status: 'status',
  scheduledFrom: (data: any) => parseMoment(data.scheduledFrom),
  scheduledTill: (data: any) => parseMoment(data.scheduledTill),
  driveBookingFleetSourceId: 'driveBookingFleetSourceId',
  driveBookingPartyName: 'driveBookingPartyName',
  lastSynchronizedBy: 'lastSynchronizedBy',
  lastSynchronizedOn: (data: any) => parseMoment(data.lastSynchronizedOn),
  driveBookingAsset: (data: any) => parseAsset(data.driveBookingAsset),
  renter: (data: any) => parseEntity(data.renter, DRIVE_RENTER_SCHEMA, DriveRenter),
  checkIn: (data: any) => parseEntity(data.checkIn, DRIVE_CHECK_IN_OUT_SCHEMA, DriveCheckInOut),
  checkOut: (data: any) => parseEntity(data.checkOut, DRIVE_CHECK_IN_OUT_SCHEMA, DriveCheckInOut),
  billings: (data: any) => parseEntities(data.billings, (item: any) => parseEntity(item, DRIVE_BILLING_SCHEMA, DriveBilling)),
  editableSettings: (data: any) => parseEntity(data.editableSettings, DRIVE_EDITABLE_SETTINGS_SCHEMA, DriveEditableSettings),
  medias: (data: any) => parseMediaFiles2(data.medias),
  comment: 'comment',
  invoiceAmount: 'invoiceAmount',
};

const DRIVE_RESERVATION_ASSET_GROUP_SCHEMA = {
  id: 'id',
  type: 'type',
  description: 'description',
  availability: 'availability',
};

const DRIVE_RESERVATION_ASSET_CLASS_SCHEMA = {
  id: 'id',
  code: 'code',
  description: 'description',
  pricing: {
    maximumMileage: 'maximumMileage',
    mileageSurplusCos: 'mileageSurplusCos',
    pricePerDay: 'pricePerDay',
    pricePerHour: 'pricePerHour',
    pricePerMonth: 'pricePerMonth',
  },
  externalClassReference: 'externalClassReference',
  groupId: 'groupId',
  availability: 'availability',
};

const DRIVE_RESERVATION_ASSET_SCHEMA = {
  id: 'id',
  description: 'description',
  brand: (data: any) => {
    // Handle brand property specifically to ensure all properties are correctly mapped
    if (!data.brand) return { name: 'Unknown Brand' };

    return {
      name: data.brand.name || 'Unknown Brand',
      logoUrl: data.brand.logoUrl || '',
      darkModeLogoUrl: data.brand.darkModeLogoUrl || '',
      icon: data.brand.icon || '', // Add icon property from mock data
    };
  },
  identifier: 'identifier',
  registrationNumber: 'registrationNumber',
  externalClassReference: 'externalClassReference',
  groupId: 'groupId',
  classId: 'classId',
};

const DRIVE_RESERVATION_SETTINGS_SCHEMA = {
  groups: 'groups',
  classes: 'classes',
  assets: 'assets',
};

const DRIVE_AVAILABLE_ASSETS_SCHEMA = {
  groups: (data: any) => parseReservationGroup(data.groups),
  classes: (data: any) => parseReservationClass(data.classes),
  assets: (data: any) => parseReservationAsset(data.assets),
  visibilitySettings: (data: any) => parseReservationSettings(data.visibilitySettings),
  bookingSettings: (data: any) => parseReservationSettings(data.bookingSettings),
};

export function parseReservationGroup(data: any): DriveReservationAssetGroup | null {
  return parseEntity(data, DRIVE_RESERVATION_ASSET_GROUP_SCHEMA, DriveReservationAssetGroup);
}

export function parseReservationClass(data: any): DriveReservationAssetClass | null {
  return parseEntity(data, DRIVE_RESERVATION_ASSET_CLASS_SCHEMA, DriveReservationAssetClass);
}

export function parseReservationAsset(data: any): DriveReservationAsset | null {
  if (!data) return null;

  const asset = parseEntity(data, DRIVE_RESERVATION_ASSET_SCHEMA, DriveReservationAsset);

  return asset;
}

export function parseReservationSettings(data: any): DriveReservationSettings | null {
  return parseEntity(data, DRIVE_RESERVATION_SETTINGS_SCHEMA, DriveReservationSettings);
}

export function parseDriveReservationAssets(data: any) {
  if (!data) {
    return null;
  }

  const result = parseEntity(data, DRIVE_AVAILABLE_ASSETS_SCHEMA, DriveAvailableAssets);

  return result;
}

export function parseAsset(data: any): DriveAsset | null {
  return parseEntity(data, DRIVE_ASSET_SCHEMA, DriveAsset);
}

export function parseDriveBooking(data: any): DriveBooking | null {
  return parseEntity(data, DRIVE_BOOKING_SCHEMA, DriveBooking);
}

export function parseDriveBookings(data: any): Array<DriveBooking> {
  return parseEntities(data, parseDriveBooking);
}

export function parseSyncResponse(data: any): SyncResponse {
  return SyncResponse.create(
    !!data?.synchronizationSuccessful,
    !!data?.dataHasChanged,
    data?.lastSynchronizedOn ? parseMoment(data.lastSynchronizedOn) : null,
    data?.lastSynchronizedBy || null,
  );
}

export function parseDriveBookingDetails(data: any): DriveBookingDetails | null {
  return parseEntity(data, DRIVE_BOOKING_DETAILS_SCHEMA, DriveBookingDetails);
}
